import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createSlots as _createSlots, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" }

import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useAuthStore, useFilterStore, useUiStore } from '@/stores';
import { IntegrationService, MetaIntegrationService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import { IntegrationWidget } from './components';
import { LevelModel, IntegrationModel, IntegrationType, MetaConnectionStatus } from '@/types';

import { LinkIcon } from '@heroicons/vue/solid';


export default /*@__PURE__*/_defineComponent({
  __name: 'IntegrationPage',
  setup(__props) {

const router = useRouter();
const { t } = useI18n();
const filterStore = useFilterStore();
const uiStore = useUiStore();
const authStore = useAuthStore();
const hasWhatsAppMeta = computed(() => authStore.userModel?.hasWhatsAppMeta === true);

const integrations: Ref<IntegrationModel[]> = ref([]);
const whatsappMetaStatus: Ref<MetaConnectionStatus> = ref(null);

const whatsappMetaWidget = computed(() => ({
    logoPath: '/img/integration/whatsapp.svg',
    contentText: t('whatsappMeta.connect.description'),
    isActive:
        whatsappMetaStatus.value === null ? undefined : whatsappMetaStatus.value === 'connected',
}));

const integrationInfo = {
    [IntegrationType.Pipedrive]: {
        logoPath: '/img/integration/pipedrive.png',
        description: t('integration.integrations.pipedrive.description'),
    },
    [IntegrationType.OpenAi]: {
        logoPath: '/img/integration/openai.png',
        description: t('integration.integrations.openai.description'),
    },
    [IntegrationType.Moskit]: {
        logoPath: '/img/integration/moskit.png',
        description: t('integration.integrations.moskit.description'),
    },
};

const configuredIntegrations = computed<IntegrationModel[]>(() =>
    integrations.value.filter((integration) => integrationInfo[integration.type]),
);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getWhatsAppMetaStatus = async () => {
    try {
        const { data } = await MetaIntegrationService.getConnectionStatus();
        whatsappMetaStatus.value = data.status;
    } catch (error) {
        toastServiceError(error);
    }
};

const getIntegrations = async () => {
    try {
        await setLoading(true);
        const response = await IntegrationService.getAll<Array<IntegrationModel>>();
        integrations.value = response.data;
        if (hasWhatsAppMeta.value) await getWhatsAppMetaStatus();
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const goToConnectWhatsAppMeta = () => router.push({ name: 'ListWhatsAppMeta' });

const handleConfigClick = (integration: IntegrationModel) => {
    router.push({
        name: 'EditIntegrationById',
        params: {
            type: integration.type,
            id: integration.id,
        },
    });
};

const handleViewLogsClick = (integration: IntegrationModel) => {
    router.push({
        name: 'ViewIntegrationLogs',
        params: {
            type: integration.type,
            id: integration.id,
        },
    });
};

const handleActivateClick = (integration: IntegrationModel) => {
    router.push({
        name: 'CreateIntegration',
        params: {
            type: integration.type,
        },
    });
};

watch([levelFilter], getIntegrations, { deep: true });

onMounted(getIntegrations);

return (_ctx: any,_cache: any) => {
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(LinkIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('core.entity.Integrations')), 1)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(IntegrationWidget), {
        isActive: whatsappMetaWidget.value.isActive,
        logoPath: whatsappMetaWidget.value.logoPath,
        contentText: whatsappMetaWidget.value.contentText,
        onConfigClick: goToConnectWhatsAppMeta,
        onViewLogsClick: goToConnectWhatsAppMeta,
        onActivateClick: goToConnectWhatsAppMeta
      }, _createSlots({ _: 2 }, [
        (!hasWhatsAppMeta.value)
          ? {
              name: "unavailable",
              fn: _withCtx(() => [
                _cache[0] || (_cache[0] = _createTextVNode(" Esta integração não está habilitada para sua operação. Entre em contato com seu comercial. ", -1))
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["isActive", "logoPath", "contentText"]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(configuredIntegrations.value, (integration, index) => {
        return (_openBlock(), _createBlock(_unref(IntegrationWidget), {
          key: index,
          isActive: integration.isActive,
          logoPath: integrationInfo[integration.type].logoPath,
          contentText: integrationInfo[integration.type].description,
          onConfigClick: ($event: any) => (handleConfigClick(integration)),
          onViewLogsClick: ($event: any) => (handleViewLogsClick(integration)),
          onActivateClick: ($event: any) => (handleActivateClick(integration))
        }, null, 8, ["isActive", "logoPath", "contentText", "onConfigClick", "onViewLogsClick", "onActivateClick"]))
      }), 128))
    ])
  ]))
}
}

})